import { apiRequest } from "./api";
import type {
  BusinessListingApplicationListItemResponse,
  BusinessListingApplicationMessageRequest,
  BusinessListingApplicationMessageResponse,
  BusinessListingApplicationRequest,
  BusinessListingApplicationResponse,
  BusinessListingMarketplaceItemResponse,
  BusinessListingResponse,
  CreateBusinessListingCheckoutSessionResponse,
  CreateBusinessListingRequest,
  TeenBusinessListingApplicationListItemResponse,
} from "../types/businessListingTypes";
import type { DistanceFilter } from "../components/gigs/TableFilters";

export const businessListingsApi = {
  listMine(): Promise<BusinessListingResponse[]> {
    return apiRequest<BusinessListingResponse[]>("/api/adult/business-listings", {
      method: "GET",
    });
  },

  getById(listingId: string): Promise<BusinessListingResponse> {
    return apiRequest<BusinessListingResponse>(`/api/adult/business-listings/${listingId}`, {
      method: "GET",
    });
  },

  closeListing(listingId: string): Promise<BusinessListingResponse> {
    return apiRequest<BusinessListingResponse>(`/api/adult/business-listings/${listingId}/close`, {
      method: "POST",
    });
  },

  createDraft(payload: CreateBusinessListingRequest): Promise<BusinessListingResponse> {
    return apiRequest<BusinessListingResponse>("/api/adult/business-listings", {
      method: "POST",
      body: payload,
    });
  },

  updateListing(listingId: string, payload: CreateBusinessListingRequest): Promise<BusinessListingResponse> {
    return apiRequest<BusinessListingResponse>(`/api/adult/business-listings/${listingId}`, {
      method: "PUT",
      body: payload,
    });
  },

  createPublicationCheckoutSession(payload: CreateBusinessListingRequest): Promise<CreateBusinessListingCheckoutSessionResponse> {
    return apiRequest<CreateBusinessListingCheckoutSessionResponse>("/api/adult/business-listings/publication-checkout-session", {
      method: "POST",
      body: payload,
    });
  },

  listMarketplace(search?: string, distance?: DistanceFilter): Promise<BusinessListingMarketplaceItemResponse[]> {
    const params = new URLSearchParams();
    if (search && search.trim().length > 0) {
      params.set("search", search.trim());
    }
    if (distance && distance !== "ANY") {
      params.set("radiusMiles", distance);
    }
    const query = params.toString();
    return apiRequest<BusinessListingMarketplaceItemResponse[]>(`/api/business-listings/marketplace${query ? `?${query}` : ""}`, {
      method: "GET",
    });
  },

  applyAsTeen(listingId: string, payload: BusinessListingApplicationRequest = {}): Promise<BusinessListingApplicationResponse> {
    return apiRequest<BusinessListingApplicationResponse>(`/api/teen/business-listings/${listingId}/applications`, {
      method: "POST",
      body: payload,
    });
  },

  listApplicationsForTeen(): Promise<TeenBusinessListingApplicationListItemResponse[]> {
    return apiRequest<TeenBusinessListingApplicationListItemResponse[]>("/api/teen/business-listing-applications", {
      method: "GET",
    });
  },

  withdrawApplicationAsTeen(applicationId: string): Promise<BusinessListingApplicationResponse> {
    return apiRequest<BusinessListingApplicationResponse>(`/api/teen/business-listing-applications/${applicationId}/withdraw`, {
      method: "POST",
    });
  },

  listApplicationsForBusiness(listingId: string): Promise<BusinessListingApplicationListItemResponse[]> {
    return apiRequest<BusinessListingApplicationListItemResponse[]>(`/api/adult/business-listings/${listingId}/applications`, {
      method: "GET",
    });
  },

  removeApplicant(applicationId: string, reason?: string): Promise<BusinessListingApplicationResponse> {
    return apiRequest<BusinessListingApplicationResponse>(`/api/adult/business-listing-applications/${applicationId}/remove`, {
      method: "POST",
      body: reason && reason.trim().length > 0 ? { reason: reason.trim() } : {},
    });
  },

  listApplicationMessagesForTeen(applicationId: string): Promise<BusinessListingApplicationMessageResponse[]> {
    return apiRequest<BusinessListingApplicationMessageResponse[]>(`/api/teen/business-listing-applications/${applicationId}/messages`, {
      method: "GET",
    });
  },

  sendApplicationMessageAsTeen(
    applicationId: string,
    payload: BusinessListingApplicationMessageRequest,
  ): Promise<BusinessListingApplicationMessageResponse> {
    return apiRequest<BusinessListingApplicationMessageResponse>(`/api/teen/business-listing-applications/${applicationId}/messages`, {
      method: "POST",
      body: payload,
    });
  },

  listApplicationMessagesForBusiness(applicationId: string): Promise<BusinessListingApplicationMessageResponse[]> {
    return apiRequest<BusinessListingApplicationMessageResponse[]>(`/api/adult/business-listing-applications/${applicationId}/messages`, {
      method: "GET",
    });
  },

  sendApplicationMessageAsBusiness(
    applicationId: string,
    payload: BusinessListingApplicationMessageRequest,
  ): Promise<BusinessListingApplicationMessageResponse> {
    return apiRequest<BusinessListingApplicationMessageResponse>(`/api/adult/business-listing-applications/${applicationId}/messages`, {
      method: "POST",
      body: payload,
    });
  },
};
